import './signUpPage.scss';

import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { isEmpty, toString, trim } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import logo from '../../assets/logo-default.svg';
import { PhoneNumberInput } from '../../components/shared_new/PhoneNumberInput/PhoneNumberInput';
import useApiStore from '../../store/apiStore/useApiStore';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UserExistsExceptionCode } from '../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';
import {
  emailValidator,
  nameValidator,
} from '../../utils/component/inputValidators';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();

  const { t } = useTranslation();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const { userExists } = useUserStore();

  const { firstName, lastName, primaryPhoneNumber, primaryEmailAddress } =
    useSignUpStore();

  const form = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const subtitle = useFeatureFlag(FeatureFlag.ENABLE_REFANDI_SIGN_UP_COPY)
    ? t('SignUpPage.refandiSubtitle')
    : t('SignUpPage.subtitle');

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    } = values;

    useSignUpStore.setState({
      firstName: firstNameValue,
      lastName: lastNameValue,
      primaryPhoneNumber: primaryPhoneNumberValue,
      primaryEmailAddress: primaryEmailAddressValue,
    });

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    if (userExistsException === UserExistsExceptionCode.BOTH_NOT_FOUND) {
      await deliverPin({
        deliverPinLocation: DeliverPinLocation.SIGN_IN,
        phone: primaryPhoneNumberValue,
        purpose: AuthType.ACCOUNT_REGISTRATION_USING_PIN,
      });

      const { deliverPinSuccess } = usePinStore.getState();

      if (deliverPinSuccess) {
        navigate('/sign-up/pin-verify');
      }
    } else {
      navigate('/sign-up/returning-user');
    }
  };

  return (
    <div className="sign-up-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.signUp.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <Link
              data-testid={locator.pages.signUp.header.signInLink}
              to="/sign-in"
            >
              {t('SignUpPage.signin')}
            </Link>
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signUp.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUp.form.h1}>
                  {t('SignUpPage.title')}
                </h1>
                <h2 data-testid={locator.pages.signUp.form.h2}>{subtitle}</h2>

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.signUp.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('firstNameValue', firstNameValidation)}
                        defaultValue={firstName}
                        error={!isEmpty(errors.firstNameValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.signUp.form.firstNameInput,
                        }}
                        label={t('SignUpPage.firstName')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.firstNameValue) && (
                        <span
                          data-testid={
                            locator.pages.signUp.form.firstNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.firstNameValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('lastNameValue', lastNameValidation)}
                        defaultValue={lastName}
                        error={!isEmpty(errors.lastNameValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.signUp.form.lastNameInput,
                        }}
                        label={t('SignUpPage.lastName')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.lastNameValue) && (
                        <span
                          data-testid={
                            locator.pages.signUp.form.lastNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.lastNameValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <PhoneNumberInput
                      form={form}
                      inputLabelKey={'SignInPage.phoneNumber'}
                      inputName="primaryPhoneNumberValue"
                      inputProps={{
                        'data-testid': locator.pages.signUp.form.phoneInput,
                      }}
                      value={primaryPhoneNumber}
                    />

                    {!isEmpty(errors.primaryPhoneNumberValue) && (
                      <span
                        data-testid={locator.pages.signUp.form.phoneInputError}
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.primaryPhoneNumberValue.message)}
                      </span>
                    )}
                  </div>
                  <div>
                    <TextField
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      defaultValue={primaryEmailAddress}
                      error={!isEmpty(errors.primaryEmailAddressValue)}
                      inputProps={{
                        'data-testid': locator.pages.signUp.form.emailInput,
                      }}
                      label={t('SignUpPage.email')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.primaryEmailAddressValue) && (
                      <span
                        data-testid={locator.pages.signUp.form.emailInputError}
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.primaryEmailAddressValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={locator.pages.signUp.form.submitButton}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('SignUpPage.getStarted')}
                </LoadingButton>

                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={locator.pages.signUp.form.termsLink}
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={locator.pages.signUp.form.privacyLink}
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'SignUpPage.termsPrivacy'}
                  />
                </p>
              </div>
              <footer data-testid={locator.pages.signUp.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpPage';
